import { ReactNode, RefObject, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Medialink, validateAndGetLinkFromMedialink } from '../../../../../api/medialink';
import { Module } from '../../../../../api/module';
import { isLti, UserMaterial, validateAndGetLinkFromUserMaterial } from '../../../../../api/user-material';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../../constants/analyticsEvents';
import ModalContext from '../../../../../contexts/ModalContext';
import useAnalytics from '../../../../../hooks/useAnalytics';
import usePlatformName from '../../../../../hooks/usePlatformName';
import LtiModal from '../../../../../shared-components/elementis/components/lti-modal';
import { buildBookWidgetUrl } from '../../../../../shared-components/elementis/util/buildBookWidgetsUrl';
import HorizontalScrollContextProps from '../../../../../shared-components/table/context/HorizontalScrollContext';
import { TOKEN_ACCESSOR } from '../../../../../utils/tokenAccessor';
import useMedialinks from '../../../toc/hooks/medialink/useMedialinks';
import useUserMaterial from '../../../toc/hooks/user-material/useUserMaterial';
import { getSessions, Session } from '../../api/history';
import { ScorableItem } from '../../api/scorable-items';
import HistoryTableHeader from './HistoryTableHeader';
import HistoryTableBody from './HistoryTableBody';
import HistoryTableBodyAdaptiveExercise from './HistoryTableBodyAdaptiveExercise';

interface Props {
  sessionsData: Awaited<ReturnType<typeof getSessions>>;
  scorableItem: ScorableItem;
  isAssignment: boolean;
  userMaterial?: UserMaterial;
  materialModule: Module;
  headerTags: ReactNode;
  medialink?: Medialink;
  module: Module;
  tableRef: RefObject<HTMLTableElement>;
}

export default function HistoryTable({
  sessionsData,
  scorableItem,
  isAssignment,
  userMaterial,
  materialModule,
  headerTags,
  medialink,
  tableRef,
}: Props) {
  const {
    REACT_APP_LTI_AUTH_URL,
    REACT_APP_BOOKWIDGETS_REVIEW_URL,
    REACT_APP_P_STUDIO_LANGUAGE,
    REACT_APP_P_STUDIO_GROUP,
  } = process.env;

  const { t } = useTranslation();
  const platformName = usePlatformName();
  const { openReview } = useMedialinks();
  const { capture } = useAnalytics();
  const { openMaterial } = useUserMaterial();

  const { showModal, closeModal } = useContext(ModalContext);
  const { horizontalScroll } = useContext(HorizontalScrollContextProps);

  async function onMaterialAccessed(session: Session) {
    if (!materialModule) return;

    if (medialink && scorableItem.type === 'medialink') {
      const node = medialink.hierarchy[medialink.hierarchy.length - 1];

      const modal = await openReview({
        medialink,
        selectedModule: materialModule,
        sessionId: session.session!,
        session: session,
        sessionStudentName: sessionsData.memberName,
        subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_RESULTS,
        nodeWithoutDescription: node,
      });

      if (!modal) return;

      return showModal(modal);
    }

    if (!userMaterial) return;
    if (isLti(userMaterial) && 'userMaxScore' in userMaterial) {
      capture({
        eventType: ANALYTICS_EVENT_TYPES.USER_MATERIAL_ACCESSED,
        subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_RESULTS,
        objectId: scorableItem.id,
      });

      const ltiUserMaterial = userMaterial.assignment
        ? { ...userMaterial, assignmentId: userMaterial.assignment.id }
        : userMaterial;

      const buildOptions: Parameters<typeof buildBookWidgetUrl>[0] = {
        platformName,
        ltiUserMaterial,
        ltipsUrl: REACT_APP_LTI_AUTH_URL,
        module: materialModule,
        studioLanguage: REACT_APP_P_STUDIO_LANGUAGE,
        reviewUrl: REACT_APP_BOOKWIDGETS_REVIEW_URL,
        audience: userMaterial.assignment,
      };

      const MODAL_ID = 'lti-modal';

      return showModal(
        <LtiModal
          id={MODAL_ID}
          aria-label=""
          headerProps={{
            title: t('PAGES.RESULTS.MODALS.LTI.TITLE', {
              materialName: userMaterial.name,
            }),
            titleTooltip: t('PAGES.RESULTS.MODALS.LTI.TITLE', {
              materialName: userMaterial.name,
            }),
            tags: headerTags,
          }}
          loginUrl={buildBookWidgetUrl(buildOptions)}
          onClose={() => closeModal(MODAL_ID)}
          studioGroup={REACT_APP_P_STUDIO_GROUP}
          tokenAccessor={TOKEN_ACCESSOR}
          loadingText={{
            title: t('EXERCISE_MANAGER.BOOKWIDGET_LOADING.TITLE'),
            p1: t('EXERCISE_MANAGER.BOOKWIDGET_LOADING.P1'),
            p2: t('EXERCISE_MANAGER.BOOKWIDGET_LOADING.P2'),
          }}
        />,
      );
    }

    const modal = await openMaterial(
      userMaterial,
      userMaterial.hierarchy!,
      undefined,
      ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_RESULTS,
    );

    if (!modal) return;

    return showModal(modal);
  }

  const link =
    (userMaterial && validateAndGetLinkFromUserMaterial(userMaterial)) ||
    (medialink && validateAndGetLinkFromMedialink(medialink)) ||
    undefined;

  const isAdaptive = Boolean(medialink?.assessmentQExercise?.isAdaptive);

  return (
    /* eslint-disable local-rules/table-accessible-name */
    <table className="history-table" ref={tableRef}>
      <HistoryTableHeader isAssignment={isAssignment} isAdaptive={isAdaptive} />
      {isAdaptive ? (
        <HistoryTableBodyAdaptiveExercise
          horizontalScroll={horizontalScroll}
          isAssignment={isAssignment}
          onMaterialAccessed={onMaterialAccessed}
          scorableItem={scorableItem}
          sessionsData={sessionsData}
        />
      ) : (
        <HistoryTableBody
          horizontalScroll={horizontalScroll}
          isAssignment={isAssignment}
          link={link}
          onMaterialAccessed={onMaterialAccessed}
          scorableItem={scorableItem}
          sessionsData={sessionsData}
        />
      )}
    </table>
  );
}
